import { Banner } from "@/components/flexkit/Banner";
import { Button } from "@/components/flexkit/Button";
import {
    DialogBody,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { CopyableInput } from "@/components/podkit/forms/CopyableInput";
import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/podkit/select/Select";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import { DisclosureStepList } from "@/components/flexkit/disclosure-step/DisclosureStep";
import { SCIMTokenInput } from "@/routes/settings/login/SCIMTokenInput";
import {
    useCreateSCIMConfiguration,
    useDeleteSCIMConfiguration,
    useUpdateSCIMConfiguration,
    type PlainSCIMConfiguration,
} from "@/queries/scim-queries";
import { useListSSOConfigurations } from "@/queries/organization-queries";
import { formatError } from "@/utils/errors";
import { SSOConfiguration_ProviderType } from "gitpod-next-api/gitpod/v1/organization_pb";
import { useCallback, useEffect, useState, type FC } from "react";
import { Controller, useForm } from "react-hook-form";

export type ConfigureSCIMProps = {
    organizationId: string;
    config?: PlainSCIMConfiguration;
    initialState?: ConfigureSCIMState;
    onClose?: () => void;
};

type SCIMForm = {
    name: string;
    ssoConfigurationId: string;
};

type ConfigureSCIMState = "configure" | "display-token" | "display-regenerated" | "confirm-delete";

export const ConfigureSCIM: FC<ConfigureSCIMProps> = ({ organizationId, config, initialState, onClose }) => {
    const { toast } = useToast();
    const [state, setState] = useState<ConfigureSCIMState>(initialState ?? "configure");
    const shouldCloseOnCancelDelete = initialState === "confirm-delete";
    const [token, setToken] = useState<string>("");

    const isEdit = !!config;
    const isTokenVisible = state === "display-token" || state === "display-regenerated";
    const currentStep = isTokenVisible ? 1 : 0;

    const { data: ssoConfigs = [], isLoading: isLoadingSSOConfigs } = useListSSOConfigurations();
    const customSSOConfigs = ssoConfigs.filter((config) => config.providerType === SSOConfiguration_ProviderType.CUSTOM);
    const hasSSOConfigs = customSSOConfigs.length > 0;

    const {
        register,
        control,
        handleSubmit,
        formState: { errors, isValid, isDirty },
    } = useForm<SCIMForm>({
        mode: "all",
        defaultValues: config
            ? {
                  name: config.name || "",
                  ssoConfigurationId: config.ssoConfigurationId || "",
              }
            : {
                  name: "",
                  ssoConfigurationId: "",
              },
    });

    const createMutation = useCreateSCIMConfiguration();
    const updateMutation = useUpdateSCIMConfiguration();
    const deleteMutation = useDeleteSCIMConfiguration();

    const [serverError, setServerError] = useState<string>();

    // Warn users before closing when token is displayed
    useTokenDisplayWarning(state);

    const handleCloseTokenDisplay = useCallback(() => {
        setToken(""); // Clear token from state
        onClose?.();
    }, [onClose]);

    const handleOnSubmit = useCallback(
        async (data: SCIMForm) => {
            setServerError(undefined);
            try {
                if (isEdit) {
                    // Update existing configuration
                    await updateMutation.mutateAsync({
                        scimConfigurationId: config.id,
                        name: data.name,
                        ssoConfigurationId: data.ssoConfigurationId || undefined,
                    });
                    toast({
                        title: "SCIM configuration updated",
                    });
                    onClose?.();
                } else {
                    // Create new configuration - returns token!
                    if (!data.ssoConfigurationId) {
                        setServerError("SSO configuration is required");
                        return;
                    }
                    const result = await createMutation.mutateAsync({
                        organizationId,
                        name: data.name,
                        ssoConfigurationId: data.ssoConfigurationId,
                    });
                    toast({
                        title: "SCIM configuration created",
                        description: "SCIM configuration created. Add the endpoint and token to your identity provider.",
                    });
                    setToken(result.token);
                    setState("display-token");
                }
            } catch (error) {
                const description = formatError(error);
                setServerError(description);
                toast({
                    title: isEdit ? "Failed to update SCIM configuration" : "Failed to create SCIM configuration",
                    description,
                });
            }
        },
        [config?.id, createMutation, isEdit, onClose, organizationId, toast, updateMutation],
    );

    const handleDelete = useCallback(async () => {
        if (!config?.id) return;
        try {
            await deleteMutation.mutateAsync(config.id);
            toast({
                title: "SCIM configuration deleted",
            });
            onClose?.();
        } catch (error) {
            toast({
                title: "Failed to delete SCIM configuration",
                description: formatError(error),
            });
        }
    }, [config?.id, deleteMutation, onClose, toast]);

    if (state === "configure" || isTokenVisible) {
        const scimEndpoint = `${window.location.origin}/scim/v2`;
        const isCreating = createMutation.isPending;
        const isUpdating = updateMutation.isPending;

        return (
            <>
                <DialogHeader>
                    <DialogTitle>{isEdit ? "Edit SCIM configuration" : "New SCIM configuration"}</DialogTitle>
                    <DialogDescription>
                        {isEdit
                            ? "Update your SCIM configuration settings."
                            : "Connect your identity provider to automatically create and manage users and groups."}
                    </DialogDescription>
                </DialogHeader>

                <DialogBody>
                    {isEdit ? (
                        <form id="scim-form" onSubmit={handleSubmit(handleOnSubmit)} className="flex flex-col gap-4">
                            <InputField label="Name" error={errors?.name?.message}>
                                <Input
                                    {...register("name", {
                                        required: "Name is required",
                                        minLength: { value: 3, message: "Name must be at least 3 characters" },
                                        maxLength: { value: 128, message: "Name must be 128 characters or fewer" },
                                    })}
                                    placeholder="Example: Google production"
                                />
                            </InputField>

                            <InputField
                                label="Linked SSO configuration"
                                hint="Choose the SSO configuration used to match SCIM users to sign-in identities."
                                error={errors?.ssoConfigurationId?.message}
                            >
                                <Controller
                                    name="ssoConfigurationId"
                                    control={control}
                                    rules={{
                                        required: !isEdit ? "SSO configuration is required" : false,
                                    }}
                                    render={({ field }) => (
                                        <Select
                                            value={field.value || undefined}
                                            onValueChange={field.onChange}
                                            disabled={!hasSSOConfigs || isLoadingSSOConfigs}
                                        >
                                            <SelectTrigger loading={isLoadingSSOConfigs}>
                                                <SelectValue
                                                    placeholder={
                                                    isLoadingSSOConfigs
                                                        ? "Loading SSO configurations"
                                                        : !hasSSOConfigs
                                                          ? "No SSO configurations available"
                                                          : "Select your SSO configuration"
                                                    }
                                                />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {customSSOConfigs.map((sso) => {
                                                    const label = sso.displayName || sso.issuerUrl || sso.id;
                                                    return (
                                                        <SelectItem key={sso.id} value={sso.id}>
                                                            <span className="text-base">{label}</span>
                                                        </SelectItem>
                                                    );
                                                })}
                                            </SelectContent>
                                        </Select>
                                    )}
                                />
                            </InputField>

                            {!hasSSOConfigs && (
                                <Banner
                                    variant="info"
                                    text="You need to create an SSO configuration first before setting up SCIM."
                                />
                            )}

                            {serverError && <Banner variant="danger" text={serverError} />}
                        </form>
                    ) : (
                        <DisclosureStepList currentStep={currentStep} onStepChange={() => undefined} canGoBack={false}>
                            <DisclosureStepList.Item
                                title="Configuration"
                                description="Choose the SSO setup this SCIM configuration should use. This determines how provisioned users match their sign-in identity."
                                completed={isTokenVisible}
                            >
                                <form id="scim-form" onSubmit={handleSubmit(handleOnSubmit)} className="flex flex-col gap-4 pb-2">
                                    <InputField label="Linked SSO configuration" error={errors?.ssoConfigurationId?.message}>
                                        <Controller
                                            name="ssoConfigurationId"
                                            control={control}
                                            rules={{
                                                required: !isEdit ? "SSO configuration is required" : false,
                                            }}
                                            render={({ field }) => (
                                                <Select
                                                    value={field.value || undefined}
                                                    onValueChange={field.onChange}
                                                    disabled={!hasSSOConfigs || isLoadingSSOConfigs || isTokenVisible}
                                                >
                                                    <SelectTrigger loading={isLoadingSSOConfigs}>
                                                        <SelectValue
                                                            placeholder={
                                                            isLoadingSSOConfigs
                                                                ? "Loading SSO configurations"
                                                                : !hasSSOConfigs
                                                                  ? "No SSO configurations available"
                                                                  : "Select your SSO configuration"
                                                            }
                                                        />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {customSSOConfigs.map((sso) => {
                                                            const label = sso.displayName || sso.issuerUrl || sso.id;
                                                            return (
                                                                <SelectItem key={sso.id} value={sso.id}>
                                                                    <span className="text-base">{label}</span>
                                                                </SelectItem>
                                                            );
                                                        })}
                                                    </SelectContent>
                                                </Select>
                                            )}
                                        />
                                    </InputField>

                                    <InputField label="Name" error={errors?.name?.message}>
                                        <Input
                                            {...register("name", {
                                                required: "Name is required",
                                                minLength: { value: 3, message: "Name must be at least 3 characters" },
                                                maxLength: { value: 128, message: "Name must be 128 characters or fewer" },
                                            })}
                                            disabled={isTokenVisible}
                                        />
                                    </InputField>

                                    {!hasSSOConfigs && (
                                        <Banner
                                            variant="info"
                                            text="You need to create an SSO configuration first before setting up SCIM."
                                        />
                                    )}

                                    {serverError && <Banner variant="danger" text={serverError} />}
                                </form>
                            </DisclosureStepList.Item>

                            <DisclosureStepList.Item
                                title="Setup endpoint and token"
                                description="Copy these values into your identity provider to complete SCIM setup."
                                completed={false}
                            >
                                {isTokenVisible && (
                                    <div className="flex flex-col gap-4 pb-2">
                                        <InputField label="SCIM endpoint URL">
                                            <CopyableInput
                                                value={scimEndpoint}
                                                disabled
                                                data-tracking-id="copy-scim-endpoint-token-display"
                                            />
                                        </InputField>

                                        <InputField label="Token">
                                            <SCIMTokenInput value={token} copyTrackingId="copy-scim-token" />
                                        </InputField>

                                        <Text className="text-sm text-content-tertiary">
                                            Treat this token like a password. It is shown only once.
                                        </Text>
                                    </div>
                                )}
                            </DisclosureStepList.Item>
                        </DisclosureStepList>
                    )}
                </DialogBody>

                <DialogFooter>
                    {isEdit && (
                        <Button
                            variant="destructive"
                            onClick={() => setState("confirm-delete")}
                            data-tracking-id="show-delete-scim-modal"
                        >
                            Delete
                        </Button>
                    )}
                    <div className="flex-grow" />
                    <Button
                        type={isTokenVisible ? "button" : "submit"}
                        form="scim-form"
                        variant="primary"
                        onClick={isTokenVisible ? handleCloseTokenDisplay : undefined}
                        disabled={
                            isTokenVisible ? !token : !isValid || (!isDirty && isEdit) || !hasSSOConfigs
                        }
                        loading={isCreating || isUpdating}
                        data-tracking-id={isTokenVisible ? "close-scim-token-display" : "submit-configure-scim"}
                    >
                        {isEdit ? "Update" : isTokenVisible ? "Close" : "Create"}
                    </Button>
                </DialogFooter>
            </>
        );
    }

    // Confirm delete state
    if (state === "confirm-delete") {
        return (
            <>
                <DialogHeader>
                    <DialogTitle>Delete SCIM Configuration?</DialogTitle>
                    <DialogDescription />
                </DialogHeader>
                <DialogBody className="flex flex-col gap-2">
                    <Text className="text-base">This will permanently delete the SCIM configuration.</Text>
                    <Text className="text-base font-medium">This action cannot be undone.</Text>
                    <Text className="text-base text-content-secondary">
                        Users provisioned via SCIM will remain in the organization but will no longer be managed through SCIM.
                    </Text>
                </DialogBody>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => (shouldCloseOnCancelDelete ? onClose?.() : setState("configure"))}
                        data-tracking-id="cancel-delete-scim"
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={handleDelete}
                        loading={deleteMutation.isPending}
                        disabled={deleteMutation.isPending}
                        data-tracking-id="confirm-delete-scim"
                    >
                        Yes, Delete
                    </Button>
                </DialogFooter>
            </>
        );
    }

    // Token display state - shown after creation or regeneration
    if (state === "display-token" || state === "display-regenerated") {
        const isRegenerated = state === "display-regenerated";
        const scimEndpoint = `${window.location.origin}/scim/v2`;

        return (
            <>
                <DialogHeader>
                    <DialogTitle>{isRegenerated ? "Token Regenerated" : "SCIM Configuration Created"}</DialogTitle>
                    <DialogDescription>
                        Save this information securely. The token will only be shown once.
                    </DialogDescription>
                </DialogHeader>

                <DialogBody className="flex flex-col gap-4">
                    <Banner variant="warning" text="Store this token securely. It will not be shown again." />

                    <InputField label="SCIM Endpoint URL" hint="Use this URL in your identity provider's SCIM settings">
                        <CopyableInput
                            value={scimEndpoint}
                            disabled
                            data-tracking-id="copy-scim-endpoint-token-display"
                        />
                    </InputField>

                    <InputField label="Bearer Token" hint="This token authenticates SCIM requests from your identity provider">
                        <CopyableInput
                            value={token}
                            disabled
                            type="password"
                            data-tracking-id="copy-scim-token"
                        />
                    </InputField>

                    {isRegenerated && (
                        <Banner
                            variant="info"
                            text="The previous token has been invalidated and will no longer work."
                        />
                    )}
                </DialogBody>

                <DialogFooter>
                    <Button variant="primary" onClick={handleCloseTokenDisplay} data-tracking-id="close-scim-token-display">
                        Done
                    </Button>
                </DialogFooter>
            </>
        );
    }

    return null;
};

/**
 * Hook to warn users before closing/navigating away when token is displayed
 */
const useTokenDisplayWarning = (state: ConfigureSCIMState) => {
    const shouldWarn = state === "display-token" || state === "display-regenerated";

    useEffect(() => {
        if (!shouldWarn) return;

        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = "Have you saved your SCIM token?";
        };

        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [shouldWarn]);
};
